/**
 * Related markets for a result card (Oct 2026): the other cards in the current
 * result list for the same game — game-level markets for a game-line card, the
 * same player's markets for a player-prop card. Reads only the cards already on
 * the list (no fetches); each chip's price is the card's best backable price
 * from the same deduped book list as the card itself (utils/cardBooks).
 *
 * Input is the renderer's ordered card list: `[cardKey, optionOrGroup]`, where a
 * group is `{ lines: [...] }` and its card shows `lines[0]`.
 */
import { cardBestPrice } from './cardBooks';
import { isPlayerPropMarket, sanitizeSelectionString } from './betCardFormatters';

const EMPTY = Object.freeze([]);

const repLine = (val) => (Array.isArray(val?.lines) ? val.lines[0] : val);
const rawOf = (line) => line?.raw || line || {};
const clean = (s) => String(sanitizeSelectionString(String(s || '')) || '').replace(/\s+/g, ' ').trim();

// ── Short market names ───────────────────────────────────────────────────────
const PERIODS = [
    [/\b(1st|first)\s+half\b|\b1h\b/, '1H', 1],
    [/\b(2nd|second)\s+half\b|\b2h\b/, '2H', 2],
    [/\b(1st|first)\s+quarter\b|\b1q\b/, '1Q', 3],
    [/\b(2nd|second)\s+quarter\b|\b2q\b/, '2Q', 4],
    [/\b(3rd|third)\s+quarter\b|\b3q\b/, '3Q', 5],
    [/\b(4th|fourth)\s+quarter\b|\b4q\b/, '4Q', 6],
    [/\b(1st|first)\s+period\b|\b1p\b/, '1P', 7],
    [/\b(2nd|second)\s+period\b|\b2p\b/, '2P', 8],
    [/\b(3rd|third)\s+period\b|\b3p\b/, '3P', 9],
    [/\b(1st|first)\s+(5|five)\s+innings\b|\bf5\b/, 'F5', 10],
    [/\b(1st|first)\s+inning\b/, '1st Inn', 11],
];

const BASES = [
    [/3[\s-]?way/, null, 0.5], // a modifier: "3-Way ML"
    [/\bmoney\s?line\b|\bml\b|match winner|\bto win\b|\bwinner\b/, 'ML', 0],
    [/\brun line\b/, 'Run Line', 1],
    [/\bpuck line\b/, 'Puck Line', 1],
    [/\bspread\b|\bhandicap\b/, 'Spread', 1],
    [/\bteam total\b/, 'Team Total', 3],
    [/\btotal\b|\bover\s*\/\s*under\b/, 'Total', 2],
    [/both teams to score|\bbtts\b/, 'BTTS', 4],
    [/draw no bet/, 'DNB', 4],
    [/double chance/, 'Dbl Chance', 4],
];

const titleCase = (s) => s.replace(/\b([a-z])/g, (m) => m.toUpperCase());

/**
 * "1st Half Moneyline" → "1H ML", "Alternate Point Spread" → "Alt Spread",
 * "Moneyline 3-Way" → "3-Way ML", "Player Points + Rebounds" → "Points + Rebounds".
 * Also returns sort ranks (period, then market type) so chips read in a steady order.
 */
export function shortMarketName(market) {
    const m = String(market || '').toLowerCase().replace(/\s+/g, ' ').trim();
    let period = '';
    let periodRank = 0;
    let rest = m;
    for (const [re, label, rank] of PERIODS) {
        if (re.test(m)) { period = label; periodRank = rank; rest = rest.replace(re, ' '); break; }
    }
    const isAlt = /\balt(ernate)?\b/.test(m);
    const threeWay = /3[\s-]?way/.test(m);
    let base = null;
    let typeRank = 5;
    if (!isPlayerPropMarket(market)) {
        for (const [re, label, rank] of BASES) {
            if (label && re.test(rest)) { base = label; typeRank = rank; break; }
        }
    }
    if (!base) {
        const stripped = rest
            .replace(/\bplayer\b/g, ' ')
            .replace(/\balt(ernate)?\b/g, ' ')
            .replace(/3[\s-]?way/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        base = titleCase(stripped) || titleCase(m) || 'Market';
        if (base.length > 22) base = `${base.slice(0, 21).trim()}…`;
    }
    if (threeWay) { base = `3-Way ${base}`; typeRank += 0.5; }
    if (isAlt) { base = `Alt ${base}`; typeRank += 0.25; }
    return { short: period ? `${period} ${base}` : base, periodRank, typeRank };
}

// ── Selection detail ("Celtics -3.5", "O 220.5") ─────────────────────────────
const MULTI_WORD_NICKNAMES = ['trail blazers', 'red sox', 'white sox', 'blue jays', 'maple leafs', 'red wings', 'blue jackets', 'golden knights'];
const nickname = (team) => {
    const t = clean(team);
    if (!t) return '';
    const lower = t.toLowerCase();
    const multi = MULTI_WORD_NICKNAMES.find((n) => lower.endsWith(n));
    if (multi) return t.slice(t.length - multi.length);
    const words = t.split(' ');
    return words[words.length - 1];
};
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function selectionDetail(line, isProp, player) {
    const raw = rawOf(line);
    let sel = clean(raw.selection || line?.selection || '');
    if (!sel) return '';
    if (isProp && player) sel = sel.replace(new RegExp(`^${escapeRe(player)}\\s*`, 'i'), '');
    for (const team of [raw.home_team, raw.away_team]) {
        const full = clean(team);
        if (full && full.includes(' ')) sel = sel.replace(new RegExp(escapeRe(full), 'i'), nickname(full));
    }
    sel = sel.replace(/\bover\b/i, 'O').replace(/\bunder\b/i, 'U').replace(/\s+/g, ' ').trim();
    return sel;
}

// ── Scope: which cards are "related" ─────────────────────────────────────────
// Player-stat words that mark a prop even without player_name ("Anytime Touchdown
// Scorer", "Batter Hits"), unless the market is a game/team market ("Total Points").
const PROP_WORDS = /\b(scorer|touchdowns?|batter|pitcher|hits|home runs?|rbis?|strikeouts|shots on goal|saves|points|rebounds|assists|threes|steals|blocks|passing|rushing|receiving|receptions|yards|goals)\b/;
const GAME_WORDS = /\b(total|team|spread|handicap|money\s?line|moneyline|run line|puck line|winner|draw|both teams)\b/;
const isPropMarket = (market) => {
    const m = String(market || '').toLowerCase();
    return isPlayerPropMarket(market) || (PROP_WORDS.test(m) && !GAME_WORDS.test(m));
};

function playerOf(line) {
    const raw = rawOf(line);
    const named = clean(raw.player_name || line?.player_name || '');
    if (named) return named;
    // "LeBron James Over 25.5" → "LeBron James"
    const sel = clean(raw.selection || line?.selection || '');
    return sel.replace(/\s*\b(over|under)\b.*$/i, '').replace(/\s*[+-]?\d+(\.\d+)?\s*$/, '').trim();
}

/** `fixture|g` for game-level markets, `fixture|p:<player>` for a player's props; null = no related row. */
export function relatedScopeOf(line) {
    if (!line) return null;
    const raw = rawOf(line);
    if (line._isCachedSGP || raw._isCachedSGP || raw.ev_method === 'individual_leg_devig') return null;
    const fixtureId = raw.fixture_id || raw.game_id || line._fixtureId;
    if (!fixtureId) return null;
    const market = raw.market || line.title || '';
    const isProp = isPropMarket(market) || !!clean(raw.player_name) || raw.player_id != null;
    if (!isProp) return `${fixtureId}|g`;
    const player = playerOf(line);
    return player ? `${fixtureId}|p:${player.toLowerCase()}` : null;
}

/** A stable DOM id for a card key (keys carry spaces and pipes). */
export function cardDomId(cardKey) {
    let h = 5381;
    const s = String(cardKey || '');
    for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
    return `bo-card-${(h >>> 0).toString(36)}`;
}

/**
 * Index over the renderer's card list.
 *   keyOf(option)      → the card key that renders this option (or null)
 *   relatedFor(key)    → chips for that card: [{ key, market, short, detail, price, label }],
 *                        full game first, then by period and market type
 *   indexOf(key)       → position in the list (for paging), -1 when absent
 */
export function buildRelatedMarkets(cards) {
    const keyByObj = new Map();
    const keyById = new Map();
    const position = new Map();
    const byScope = new Map();
    const scopeByKey = new Map();
    const chipByKey = new Map();

    (cards || []).forEach(([key, val], idx) => {
        if (!key || position.has(key)) return;
        position.set(key, idx);
        const lines = Array.isArray(val?.lines) ? val.lines : [val];
        for (const l of lines) {
            if (!l || typeof l !== 'object') continue;
            if (!keyByObj.has(l)) keyByObj.set(l, key);
            if (l.id != null && !keyById.has(l.id)) keyById.set(l.id, key);
        }
        if (val && typeof val === 'object' && !keyByObj.has(val)) keyByObj.set(val, key);

        const line = repLine(val);
        const scope = relatedScopeOf(line);
        if (!scope) return;
        scopeByKey.set(key, scope);
        if (!byScope.has(scope)) byScope.set(scope, []);
        byScope.get(scope).push(key);

        const raw = rawOf(line);
        const market = clean(raw.market || line?.title || '');
        const isProp = scope.includes('|p:');
        const { short, periodRank, typeRank } = shortMarketName(market);
        const detail = selectionDetail(line, isProp, isProp ? playerOf(line) : '');
        const price = cardBestPrice(line);
        const label = [market || short, detail, price === null ? 'no price' : `best price ${price > 0 ? '+' : ''}${price}`]
            .filter(Boolean).join(', ');
        chipByKey.set(key, { key, market, short, detail, price, label, periodRank, typeRank, idx });
    });

    const cache = new Map();
    const relatedFor = (key) => {
        if (!key || !scopeByKey.has(key)) return EMPTY;
        if (cache.has(key)) return cache.get(key);
        const out = byScope.get(scopeByKey.get(key))
            .filter((k) => k !== key)
            .map((k) => chipByKey.get(k))
            .sort((a, b) => (a.periodRank - b.periodRank) || (a.typeRank - b.typeRank) || (a.idx - b.idx))
            .map(({ periodRank, typeRank, idx, ...chip }) => chip);
        const result = out.length ? Object.freeze(out) : EMPTY;
        cache.set(key, result);
        return result;
    };

    return {
        keyOf: (option) => {
            if (!option) return null;
            if (keyByObj.has(option)) return keyByObj.get(option);
            if (option.id != null && keyById.has(option.id)) return keyById.get(option.id);
            return null;
        },
        relatedFor,
        indexOf: (key) => (position.has(key) ? position.get(key) : -1),
    };
}
